<script setup lang="ts">
import { useId } from '@/utils/useId'

/**
 * Contains unique id
 */
const id = useId()

const props = defineProps({
  label: {
    type: String,
    default: ''
  },
  placeholder: {
    type: String,
    required: false
  },
  name: {
    type: String,
    default: ''
  },
  error: {
    type: String,
    default: ''
  },
  modelValue: {
    required: true
  },
  disabled: {
    type: Boolean,
    require: false
  },
  autocomplete: {
    type: String,
    required: false
  },
  customClass: {
    type: String,
    requried: false,
    default: ''
  },
  isBoldLabel: {
    type: Boolean,
    required: false,
    default: false
  },
  labelClass: {
    type: String,
    requried: false,
    default: ''
  }
})
</script>

<template>
  <div class="my-1">
    <div class="checkbox-mystic" :class="props.customClass">
      <input
        type="checkbox"
        v-bind="$attrs"
        :id="`${id}_${props.name || props.label + props.placeholder}`"
        class=""
        :class="[
          {
            'section-primary p-small inline-block rounded-md border px-4 py-2': !props.customClass
          }
        ]"
        :placeholder="props.placeholder"
        :autocomplete="props.autocomplete || undefined"
        :value="modelValue"
        :checked="modelValue == true ? true : false"
        :disabled="props.disabled"
        @click="(event: Event) => $emit('update:modelValue', (event.target as HTMLInputElement).checked)"
      />
      <label
        :for="`${id}_${props.name || props.label + props.placeholder}`"
        class="p-small my-2 block"
        :class="[
          props.labelClass,
          {
            'font-medium': props.isBoldLabel
          }
        ]"
      >
        <span class="check-svg">
          <svg width="14px" height="13px" viewBox="0 0 12 11">
            <polyline points="1 6.29411765 4.5 10 11 1"></polyline>
          </svg>
        </span>

        <span class="check-label"> {{ props.label }}</span>
      </label>
    </div>
    <div class="relative">
      <div
        :class="[
          'pointer-events-none',
          'absolute',
          'inset-y-0',
          'right-0',
          'flex',
          'items-center',
          'pr-3',
          { hidden: !props.error }
        ]"
      >
        <FontAwesomeIcon icon="fa-circle-xmark" class="error h-5 w-5" aria-hidden="true" />
      </div>
    </div>
    <p :class="['p-xsmall ', 'error', { hidden: !props.error }]">
      {{ props.error }}
    </p>
  </div>
</template>

<style>
.checkbox-mystic label .check-svg {
  position: relative;
  display: block;
  width: 20px;
  height: 20px;
  border-radius: 4px;
  background-color: #faf7f7;
  transition: all 0.15s ease;

  -webkit-box-shadow: inset 0px 0px 1px 1px rgba(0, 0, 0, 0.25);
  -moz-box-shadow: inset 0px 0px 1px 1px rgba(0, 0, 0, 0.25);
  box-shadow: inset 0px 0px 1px 1px rgba(0, 0, 0, 0.25);
}

.checkbox-mystic label .check-svg svg {
  position: absolute;
  top: 3px;
  left: 3px;
  fill: none;
  stroke-linecap: round;
  stroke-linejoin: round;
  stroke: #fff;
  stroke-width: 2;
  stroke-dasharray: 17;
  stroke-dashoffset: 17;
  transform: translate3d(0, 0, 0);
}

.checkbox-mystic {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 0.25rem;
  user-select: none;
}

.checkbox-mystic label {
  display: flex;
  align-items: center;
  cursor: pointer;
  column-gap: 5px;
  width: max-content;
}

.checkbox-mystic input[type='checkbox'] {
  display: none;
  visibility: hidden;
}

.checkbox-mystic input[type='checkbox']:checked + label .check-svg {
  background-color: #196e9b;
  -webkit-box-shadow: none;
  -moz-box-shadow: none;
  box-shadow: none;
}

.checkbox-mystic input[type='checkbox']:checked + label .check-svg svg {
  stroke-dashoffset: 0;
  transition: all 0.15s ease;
}
</style>
